import { IConfigFunctionArgs } from 'Components/Worksheets/Core';
import {
  convertLabelMapToConfig,
  convertLabelUomMapToConfig,
  measuredUsingConfigFor,
  mergeConfig,
  wrapAsDerivedFromMeasurementsConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { FormatTypes } from 'Lib/Format';
import * as oPath from 'Lib/Object/Path/Immutable';

const getConfig = (prefix: string) =>
  mergeConfig(
    {
      [`${prefix}.Measurement.DryingTime`]: { uom: 'h', format: { nearest: 0.01 } },
      [`${prefix}.MoistureContentResults.MoistureContentPercent`]: { format: { nearest: 0.1 } },
      [`${prefix}.DryingTray.DateTimePlacedInOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },
      [`${prefix}.Measurement.DateTimeRemovedFromOvenLocal`]: {
        format: { type: FormatTypes.Time, pattern: 'YYYY-MM-DD HH:mm' },
      },
    },
    getLabelConfig(prefix),
    getMeasuredUsingConfig(prefix),
    getDerivedFromMeasurementsConfig(prefix),
    {
      [`${prefix}.Measurement.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
      [`${prefix}.MoistureContentMeasurements.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

const getLabelConfig = (prefix: string) =>
  mergeConfig(
    convertLabelMapToConfig({
      [`${prefix}.MoistureContentMeasurements.Balance`]: 'Balance',
      [`${prefix}.Measurement.Balance`]: 'Dry Mass Balance',

      [`${prefix}.MoistureContentMeasurements.ContainerLabel`]: 'Tare ID',
      [`${prefix}.MoistureContentMeasurements.ContainerMass`]: 'Tare Mass',
      [`${prefix}.MoistureContentMeasurements.WetSoilAndContainerMass`]: 'Tare & Wet Mass',
      [`${prefix}.MoistureContentMeasurements.WetSoilMass`]: 'Wet Mass',

      [`${prefix}.ReadingNumber`]: 'Dry Mass Reading Number',
      [`${prefix}.DatePlacedInOven`]: 'Date in Oven',
      [`${prefix}.TimePlacedInOven`]: 'Time in Oven',

      [`${prefix}.Measurement.DrySoilAndContainerMass`]: 'Tare & Dry Mass',
      [`${prefix}.Measurement.DrySoilMass`]: 'Dry Mass',
      [`${prefix}.Measurement.DateTimeRemovedFromOvenLocal`]: 'Date and Time Out of Oven',
      [`${prefix}.Measurement.DryingTime`]: 'Drying Time',

      [`${prefix}.MoistureContentResults.MoistureContentPercent`]: 'Moisture Content',
    }),
    convertLabelUomMapToConfig({
      [`${prefix}.MoistureContentResults.MoistureContentPercent`]: '%',
    })
  );

const getMeasuredUsingConfig = (prefix: string) => {
  const dryMassBalance = measuredUsingConfigFor(`${prefix}.Measurement.Balance`);
  const mcBalance = measuredUsingConfigFor(`${prefix}.MoistureContentMeasurements.Balance`);
  return {
    [`${prefix}.MoistureContentMeasurements.ContainerMass`]: mcBalance,
    [`${prefix}.MoistureContentMeasurements.WetSoilAndContainerMass`]: mcBalance,
    [`${prefix}.Measurement.DrySoilAndContainerMass`]: dryMassBalance,
    /**
     * We only store one balance for every dry mass reading
     * The mass before drying is measured by the normal balance if this is first drying,
     * or the shared dry mass balance for subsequent dryings
     */
    [`${prefix}.Measurement.SoilAndContainerMassBeforeDrying`]: ({ worksheet }: IConfigFunctionArgs) =>
      oPath.get(worksheet, `${prefix}.ReadingNumber`) > 1 ? dryMassBalance : mcBalance,
  };
};

const getDerivedFromMeasurementsConfig = (prefix: string) => ({
  [`${prefix}.Measurement.DrySoilMass`]: wrapAsDerivedFromMeasurementsConfig([
    `${prefix}.MoistureContentMeasurements.ContainerMass`,
    `${prefix}.Measurement.DrySoilAndContainerMass`,
  ]),
  [`${prefix}.MoistureContentMeasurements.WetSoilMass`]: wrapAsDerivedFromMeasurementsConfig([
    `${prefix}.MoistureContentMeasurements.ContainerMass`,
    `${prefix}.MoistureContentMeasurements.WetSoilAndContainerMass`,
  ]),
});

export default getConfig('data');
